import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */

class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
  super.connectedCallback();

  document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
  document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

  // 🧩 Run free gift check on initial load
  if (this.dataset.enableConditionalAtcItem === 'true') {
    this.checkConditionalAtcOnInitialLoad();
  }
}

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);
      
        const pseudoParsedState = {
        items: parsedResponseText.items || []
      };

      const { enableConditionalAtcItem } = this.getConditionalAtcItemSettings?.() || {};
        if (enableConditionalAtcItem) {
          this.handleConditionalAtcItem(pseudoParsedState);
        }

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

         // Update item count
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;


        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );
       morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        // ✅ Update the progress bar using data returned from the cart API
        if (parsedResponseText && parsedResponseText.sections) {
          fetch(`${Theme.routes.cart_url}.js`, fetchConfig('json'))
            .then((res) => res.json())
            .then((cartData) => {
              this.updateProgressBar({
                total_price: cartData.total_price,
                items_subtotal_price: cartData.items_subtotal_price
              });

              // ✅ Also re-check free gift logic
              const { enableConditionalAtcItem } = this.getConditionalAtcItemSettings?.() || {};
              if (enableConditionalAtcItem) {
                this.handleConditionalAtcItem(cartData);
              }
            });
        }

      })

      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

updateProgressBar({ total_price, items_subtotal_price }) {
  const progressWrapper = document.getElementById('cart-progress-wrapper');
  if (!progressWrapper) return;

    const cartTotalCents = progressWrapper.dataset.useItemsSubtotal === 'true'
      ? items_subtotal_price
       : total_price;
 

  const currencyFormat = progressWrapper.dataset.currencyFormat;
  const thresholds = progressWrapper.dataset.thresholds.split(',').map(Number);
  const preGoalMessages = progressWrapper.dataset.preGoalMessages.split('||');
  const postGoalMessages = progressWrapper.dataset.postGoalMessages.split('||');
  const goalPositions = progressWrapper.dataset.goalPositions.split(',').map(Number);

  const totalThreshold = thresholds[thresholds.length - 1];
  const progressPercentage = Math.min((cartTotalCents / totalThreshold) * 100, 100);

  const progressBar = document.getElementById('cart-progress-bar');
  const goalIcons = document.querySelectorAll('.goal-icon');
  const goalMessageElement = document.querySelector('.goal-message');

  if (cartTotalCents === 0) {
    progressWrapper.style.display = 'none';
    goalMessageElement.style.display = 'none';
    progressBar.style.width = '0%'; 
  } else {
    progressWrapper.style.display = 'block';
    const previousWidth = parseFloat(progressBar.style.width) || 0;
    progressBar.style.width = `${progressPercentage}%`;

    if (progressPercentage >= 100) {
      progressWrapper.classList.add('full');
    } else {
      progressWrapper.classList.remove('full');
    }

    let nextGoalIndex = -1;
    for (let i = 0; i < thresholds.length; i++) {
      if (cartTotalCents < thresholds[i]) {
        nextGoalIndex = i;
        break;
      }
    }

    goalIcons.forEach((goalIcon, index) => {
      const cartTotalDiff = cartTotalCents - thresholds[index];
      const icon = goalIcon.querySelector('img');
      const goalNumber = goalIcon.dataset.index;
      
      if (icon) {
        if (cartTotalDiff < 0) {
          const regularIconUrl = goalIcon.dataset.regularIcon;
          if (regularIconUrl) {
            icon.src = regularIconUrl;
            icon.srcset = `${regularIconUrl} 50w`;
            icon.alt = `Goal ${goalNumber}`;
          }
        } else {
          const reachedIconUrl = goalIcon.dataset.reachedIcon;
          if (reachedIconUrl) {
            icon.src = reachedIconUrl;
            icon.srcset = `${reachedIconUrl} 50w`;
            icon.alt = `Goal ${goalNumber} Reached`;
          }
        }
      }
    });

    goalMessageElement.style.display = 'block';
    if (nextGoalIndex === -1) {
      const message = postGoalMessages[postGoalMessages.length - 1];
      goalMessageElement.innerHTML = message;
    } else {
      const remainingForGoal = thresholds[nextGoalIndex] - cartTotalCents;
      const remainingAmount = remainingForGoal / 100;
      const remainingAmountFormatted = this.formatCurrency(currencyFormat, remainingAmount);
      const preGoalMessageTemplate = preGoalMessages[nextGoalIndex];
      const message = preGoalMessageTemplate.replace('[remaining_for_goal]', remainingAmountFormatted);
      goalMessageElement.innerHTML = message;
    }
  }
}

formatCurrency(currencyFormat, amount) {
  let formattedAmount = '';
  formattedAmount = currencyFormat
    .replace('{{amount}}', amount.toFixed(2)) // Standard with two decimals
    .replace('{{amount_no_decimals}}', amount.toFixed(0)) // No decimals
    .replace('{{amount_with_comma_separator}}', amount.toFixed(2).replace('.', ',')) // Replace period with comma
    .replace('{{amount_no_decimals_with_comma_separator}}', amount.toFixed(0).replace('.', ',')) // No decimals, use comma
    .replace('{{amount_with_apostrophe_separator}}', amount.toFixed(2).replace('.', "'")) // Apostrophe separator
    .replace('{{amount_no_decimals_with_space_separator}}', amount.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')) // No decimals, space
    .replace('{{amount_with_space_separator}}', amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ').replace('.', ',')) // Space separator
    .replace('{{amount_with_period_and_space_separator}}', amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')); // Period and space
  return formattedAmount;
}

formatCurrency(currencyFormat, amount) {
  let formattedAmount = '';
  formattedAmount = currencyFormat
    .replace('{{amount}}', amount.toFixed(2)) // Standard with two decimals
    .replace('{{amount_no_decimals}}', amount.toFixed(0)) // No decimals
    .replace('{{amount_with_comma_separator}}', amount.toFixed(2).replace('.', ',')) // Replace period with comma
    .replace('{{amount_no_decimals_with_comma_separator}}', amount.toFixed(0).replace('.', ',')) // No decimals, use comma
    .replace('{{amount_with_apostrophe_separator}}', amount.toFixed(2).replace('.', "'")) // Apostrophe separator
    .replace('{{amount_no_decimals_with_space_separator}}', amount.toFixed(0).replace(/\\B(?=(\\d{3})+(?!\\d))/g, ' ')) // No decimals, space
    .replace('{{amount_with_space_separator}}', amount.toFixed(2).replace(/\\B(?=(\\d{3})+(?!\\d))/g, ' ').replace('.', ',')) // Space separator
    .replace('{{amount_with_period_and_space_separator}}', amount.toFixed(2).replace(/\\B(?=(\\d{3})+(?!\\d))/g, ' ')); // Period and space
  return formattedAmount;
}


  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
isForcedCartUpdate = false;

getConditionalAtcItemSettings() {
  const enableConditionalAtcItem = this.dataset.enableConditionalAtcItem === 'true';
  const atcItemConditionType = this.dataset.atcItemConditionType || '';
  const atcItemMetaobjectHandle = this.dataset.atcItemMetaobjectHandle || '';
  const atcItemProductListCsv = this.dataset.atcItemProductList || '';
  const useAsExclusion = this.dataset.atcItemUseAsExclusion === 'true';
  const atcItemThresholdValue = parseFloat(this.dataset.atcItemThresholdValue || '0');

  const parseCsv = (csv) => csv.split(',').map(s => s.trim()).filter(Boolean);

  const atcItemVariantToAddCsv = this.dataset.atcItemVariantToAdd || '';
  const atcItemVariantToAdd = parseCsv(atcItemVariantToAddCsv);
  const atcItemProductList = parseCsv(atcItemProductListCsv);

  return {
    enableConditionalAtcItem,
    atcItemConditionType,
    atcItemMetaobjectHandle,
    atcItemProductList,
    useAsExclusion,
    atcItemVariantToAdd,
    atcItemThresholdValue
  };
}

checkConditionalAtcOnInitialLoad() {
  fetch(`${Theme.routes.cart_url}.js`, { ...fetchConfig('json') })
    .then((resp) => resp.json())
    .then((cartData) => {
      const pseudoParsedState = {
        items: cartData.items
      };
      this.handleConditionalAtcItem(pseudoParsedState);
    })
    .catch((err) => {});
}

handleConditionalAtcItem(parsedState) {
  if (this.isForcedCartUpdate) return;

  console.log('Handling conditional ATC with cart:', parsedState);

  const {
    enableConditionalAtcItem,
    atcItemConditionType,
    atcItemProductList,
    useAsExclusion,
    atcItemVariantToAdd,
    atcItemThresholdValue
  } = this.getConditionalAtcItemSettings();

  if (!enableConditionalAtcItem) return;

  let relevantCount = 0;
  let relevantTotal = 0;

  parsedState.items.forEach((item) => {
    if (atcItemVariantToAdd.includes(String(item.variant_id))) return;

    const productIdStr = String(item.product_id);
    let productEligible = atcItemProductList.length === 0
      ? true
      : useAsExclusion
        ? !atcItemProductList.includes(productIdStr)
        : atcItemProductList.includes(productIdStr);

    if (!productEligible) return;

    if (atcItemConditionType === 'cart_item_count') {
      relevantCount += item.quantity;
    } else if (atcItemConditionType === 'cart_subtotal') {
      relevantTotal += item.original_line_price;
    } else if (atcItemConditionType === 'cart_total') {
      relevantTotal += item.final_line_price;
    }
  });

  let conditionMet = false;
  if (atcItemConditionType === 'cart_item_count') {
    conditionMet = relevantCount >= atcItemThresholdValue;
  } else {
    const requiredCents = atcItemThresholdValue * 100;
    conditionMet = relevantTotal >= requiredCents;
  }

  if (!conditionMet) {
    window.userRemovedAutoAtcItem = false;
    localStorage.removeItem('userRemovedAutoAtcItemData');
  }

  if (conditionMet && window.userRemovedAutoAtcItem) return;

  const singleVariantId = atcItemVariantToAdd[0];
  if (!singleVariantId) return;

  const lineIndex = this.findLineIndexByVariantId(parsedState.items, singleVariantId);

  if (conditionMet) {
    if (lineIndex === -1) {
      this.forceCartAdd(singleVariantId, 1);
    }
  } else {
    if (lineIndex !== -1) {
      this.forceCartRemove(lineIndex + 1);
    }
  }
}

findLineIndexByVariantId(items, variantId) {
  return items.findIndex((it) => String(it.variant_id) === String(variantId));
}

forceCartAdd(variantId, quantity) {
  this.isForcedCartUpdate = true;

  const body = JSON.stringify({
    items: [{ id: variantId, quantity }]
  });

  fetch(`${Theme.routes.cart_add_url}`, fetchConfig('json', { body }))
    .then((response) => response.json())
    .then(() => this.refreshCartAfterForcedUpdate())
    .catch(() => {
      this.isForcedCartUpdate = false;
    });
}

forceCartRemove(line) {
  this.isForcedCartUpdate = true;

  const body = JSON.stringify({
    line,
    quantity: 0
  });

  fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
    .then((response) => response.text())
    .then(() => this.refreshCartAfterForcedUpdate())
    .catch(() => {
      this.isForcedCartUpdate = false;
    });
}

refreshCartAfterForcedUpdate() {
  fetch(`${Theme.routes.cart_url}?section_id=${this.sectionId}`)
    .then((resp) => resp.text())
    .then((responseText) => {
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const newCartItems = html.querySelector('cart-items-component');
      if (newCartItems) {
        this.replaceWith(newCartItems);
      }
    })
    .finally(() => {
      this.isForcedCartUpdate = false;
    });
}

}
if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}